import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import Keyboard from 'simple-keyboard';

const NUMERIC_LAYOUT = {
  default: [
    '1 2 3',
    '4 5 6',
    '7 8 9',
    '- 0 . {bksp}',
    '{enter}',
  ],
};
const NUMERIC_INPUT_PATTERN = /^-?\d*\.?\d*$/;

@Injectable({ providedIn: 'root' })
export class VirtualKeyboardService {
  activeInput: HTMLInputElement | HTMLTextAreaElement | null = null;
  visible$ = new BehaviorSubject<boolean>(false);

  private keyboard: Keyboard | null = null;

  registerKeyboard(kb: Keyboard) {
    this.keyboard = kb;
    if (this.activeInput) {
      this.configureLayout(this.activeInput);
    }
  }

  unregisterKeyboard(kb: Keyboard | undefined) {
    if (this.keyboard === kb) {
      this.keyboard = null;
    }
  }

  syncInput(value: string) {
    this.keyboard?.setInput(value);
  }

  setActiveInput(el: HTMLInputElement | HTMLTextAreaElement) {
    this.activeInput = el;
    this.configureLayout(el);
    this.visible$.next(true);
  }

  submitActiveInput() {
    this.activeInput?.dispatchEvent(new KeyboardEvent('keyup', {
      key: 'Enter',
      code: 'Enter',
      bubbles: true,
    }));
  }

  focusNextInput() {
    const currentInput = this.activeInput;
    if (!(currentInput instanceof HTMLInputElement)) {
      this.hide();
      return;
    }

    const homePage = currentInput.closest('app-precision');
    const inputs = Array.from(
      (homePage ?? document).querySelectorAll<HTMLInputElement>('input[appVirtualKeyboard]'),
    ).filter(input => this.isFocusableInput(input));
    const nextInput = inputs[inputs.indexOf(currentInput) + 1];

    if (!nextInput) {
      this.hide();
      return;
    }

    nextInput.focus();
    this.setActiveInput(nextInput);
    this.syncInput(nextInput.value);
  }

  hide() {
    this.visible$.next(false);
    this.activeInput = null;
  }

  private configureLayout(input: HTMLInputElement | HTMLTextAreaElement) {
    const isNumberInput = input instanceof HTMLInputElement && input.type === 'number';
    this.keyboard?.setOptions({
      layout: isNumberInput ? NUMERIC_LAYOUT : undefined,
      layoutName: 'default',
      inputPattern: isNumberInput ? NUMERIC_INPUT_PATTERN : undefined,
    });
  }

  private isFocusableInput(input: HTMLInputElement): boolean {
    const style = getComputedStyle(input);
    return input.isConnected
      && input.type !== 'hidden'
      && !input.disabled
      && !input.closest('.ion-page-hidden')
      && style.display !== 'none'
      && style.visibility !== 'hidden';
  }
}
